import { datadogRum } from "@datadog/browser-rum";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError } from "@src/appV2/lib/analytics/log";
import { Platform } from "@src/mobile/utils/platform";
// eslint-disable-next-line no-restricted-imports -- This is the lock wrapper; it must import the raw function.
import { fetchAuthSession as amplifyFetchAuthSession } from "aws-amplify/auth";

import { readCapacitorCognitoSession } from "./capacitorCognitoSession";
import { hasFreshStoredIdToken } from "./storedCognitoIdToken";

const LOCK_NAME = "cognito-token-refresh";

/**
 * Threshold after which a `fetchAuthSessionWithLock` call is considered hung
 * for diagnostic purposes. Emits a `LOCK_HANG_DETECTED` action with the
 * current phase so we can correlate stalls against Web Locks vs Amplify.
 */
const HANG_DETECTION_MS = 8000;

/**
 * Maximum time we will block waiting to acquire the Web Lock. If exceeded,
 * we abort the request and fall back to calling Amplify directly — accepting
 * a small cross-tab refresh-race risk rather than letting hidden-tab
 * throttling stall every request in the tab.
 *
 * See DEVOP-5298: backgrounded Chromium tabs freeze the lock holder's
 * `await`, leaving the lock held for many minutes.
 */
const LOCK_ACQUIRE_TIMEOUT_MS = 8000;

/**
 * Maximum time we will wait for Amplify's `fetchAuthSession` to settle. If
 * exceeded, we reject so callers see a normal error path instead of an
 * indefinite XHR freeze.
 */
const AMPLIFY_FETCH_TIMEOUT_MS = 10_000;

type LockPhase = "awaiting_lock" | "awaiting_amplify";

/**
 * How the Amplify refresh was ultimately reached:
 * - `locked`: we held the cross-tab Web Lock for the refresh (the safe path).
 * - `bypassed_no_locks`: the Web Locks API was unavailable, so we called
 *   Amplify directly with only Amplify's intra-tab dedupe protecting us.
 * - `bypassed_acquire_timeout`: we waited `LOCK_ACQUIRE_TIMEOUT_MS` for the
 *   lock (typically because a frozen/backgrounded tab held it), gave up, and
 *   called Amplify directly.
 *
 * The two `bypassed_*` outcomes are the only paths where a concurrent tab can
 * race us onto the same refresh token. Recording the outcome on failures lets
 * us tell whether residual `RefreshTokenReuseException`s come from the
 * deliberately-accepted bypass race or from a genuine defect on the locked
 * path (which would mean the lock failed its one job). See staff-1344.
 *
 * - `cached_no_lock`: a comfortably-unexpired ID token was already in
 *   localStorage, so Amplify serves the cached session without a network
 *   refresh — no rotation happens, hence nothing for the lock to guard.
 */
type LockOutcome = "locked" | "bypassed_no_locks" | "bypassed_acquire_timeout" | "cached_no_lock";

/**
 * `error.name` Amplify assigns to the Cognito service error thrown when a
 * rotated (already-consumed) refresh token is presented. Cognito returns it as
 * the `x-amzn-errortype` / `__type`, which Amplify's `parseJsonError` copies
 * verbatim onto `error.name`. Used to single out the cross-tab refresh race
 * from other auth failures.
 */
const REFRESH_TOKEN_REUSE_ERROR_NAME = "RefreshTokenReuseException";

function isRefreshTokenReuseError(error: unknown): boolean {
  return error instanceof Error && error.name === REFRESH_TOKEN_REUSE_ERROR_NAME;
}

/**
 * Message thrown when Amplify's `fetchAuthSession` exceeds
 * `AMPLIFY_FETCH_TIMEOUT_MS`. Kept stable so existing Datadog queries and
 * monitors that key off the error message continue to match.
 */
const AMPLIFY_FETCH_TIMEOUT_MESSAGE = "amplify_fetch_auth_session_timeout";

/**
 * Distinguishes the wrapper's own timeout from a genuine Amplify/Web Locks
 * failure. The timeout is a deliberate, self-induced control-flow signal that
 * is already surfaced as the `COGNITO_SESSION_AMPLIFY_TIMEOUT` RUM action, so
 * the outer handler records it as a non-error action rather than re-reporting
 * it to Error Tracking. See staff-1324.
 */
class AmplifyAuthSessionTimeoutError extends Error {
  constructor() {
    super(AMPLIFY_FETCH_TIMEOUT_MESSAGE);
    this.name = "AmplifyAuthSessionTimeoutError";
  }
}

/**
 * Cross-tab lock for Cognito token refresh using the Web Locks API.
 *
 * AWS Cognito uses refresh-token rotation: once a refresh token is consumed,
 * the old one is invalidated. Amplify v6 already deduplicates concurrent
 * `fetchAuthSession` calls **within a single tab** via `deDupeAsyncFunction`.
 * However, when multiple browser tabs trigger a refresh simultaneously, each
 * tab sends the same refresh token independently — the second tab receives a
 * `RefreshTokenReuseException`, which clears tokens and signs the user out.
 *
 * This module wraps `fetchAuthSession` with `navigator.locks.request` so that
 * only one tab can refresh at a time. While one tab holds the lock and
 * refreshes tokens, other tabs wait. When the waiting tab acquires the lock
 * the tokens in `localStorage` are already fresh, so Amplify reads them
 * without issuing another network refresh.
 *
 * Falls back to calling `fetchAuthSession` directly when the Web Locks API
 * is not available (e.g., non-secure contexts).
 *
 * On Capacitor, this delegates to `readCapacitorCognitoSession` which
 * bypasses Amplify entirely. See that file for the rationale.
 */

type AuthSessionResult = Awaited<ReturnType<typeof amplifyFetchAuthSession>>;
type FetchAuthSessionOptions = Parameters<typeof amplifyFetchAuthSession>[0];

function generateCorrelationId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Drop-in replacement for Amplify's `fetchAuthSession` that coordinates
 * token refreshes across browser tabs via the Web Locks API.
 *
 * Within a single tab, Amplify's built-in `deDupeAsyncFunction` already
 * prevents duplicate refresh requests. This wrapper adds the cross-tab
 * guarantee: only one tab holds the lock at a time, so concurrent tabs
 * never race with the same refresh token.
 *
 * On Capacitor, returns a shape-compatible session read directly from
 * localStorage (see `capacitorCognitoSession.ts`).
 *
 * Emits diagnostic events only when something is wrong: hang detection
 * (any phase exceeds `HANG_DETECTION_MS`), Amplify timeouts, lock
 * bypasses (no Web Locks API or acquire timeout), and failures. The
 * happy path is silent.
 */
export async function fetchAuthSessionWithLock(
  options?: FetchAuthSessionOptions
): Promise<AuthSessionResult> {
  if (Platform.isMobile) {
    // The cast is safe: `CapacitorAuthSession` is a structural subset of
    // `AuthSessionResult` that contains all the fields callers actually use
    // (`tokens.idToken.payload`, `tokens.idToken.toString()`,
    // `tokens.accessToken`, `tokens.refreshToken`). Capacitor never has
    // `credentials`/`identityId`/`userSub` because we don't use Amplify's
    // identity-pool flow there.
    return (await readCapacitorCognitoSession(options)) as AuthSessionResult;
  }

  const correlationId = generateCorrelationId();
  const startMs = Date.now();
  const forceRefresh = options?.forceRefresh ?? false;
  const hasLocks = typeof navigator !== "undefined" && "locks" in navigator;
  const isHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
  let phase: LockPhase = "awaiting_lock";
  // Assume the safe path until `runFetchAuthSession` tells us a bypass fired.
  // A `RefreshTokenReuseException` is only thrown from inside Amplify's refresh,
  // which always runs after the `onAcquired` callback updates this — so by the
  // time we classify a reuse failure below, this value is accurate.
  let lockOutcome: LockOutcome = "locked";

  const logContext = {
    correlationId,
    forceRefresh,
    hasLocks,
    isHidden,
  };

  const hangTimer = setTimeout(() => {
    datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_HANG_DETECTED, {
      ...logContext,
      phase,
      elapsedMs: Date.now() - startMs,
      thresholdMs: HANG_DETECTION_MS,
    });
  }, HANG_DETECTION_MS);

  try {
    // Fast path: the lock's sole job is to serialize refresh-token rotation
    // across tabs. When a comfortably-unexpired ID token is already stored,
    // Amplify serves the cached session without rotating anything, so there is
    // no race to guard — and skipping the lock matters: with every request of
    // every tab serialized through one exclusive Web Lock, a single throttled
    // background tab holding it stalled all requests browser-wide by 8s+
    // (100k+ `awaiting_lock` hang events/week in RUM). Only genuine
    // refreshes (expired/near-expiry token or forceRefresh) take the lock.
    if (!forceRefresh && hasFreshStoredIdToken()) {
      lockOutcome = "cached_no_lock";
      phase = "awaiting_amplify";
      return await amplifyFetchAuthSessionWithTimeout(options);
    }

    // Happy-path lock/Amplify RUM actions were dropped in DEVOP-5513 (#6880) to
    // cut non-error noise; the callback now only advances `phase` and records
    // the `lockOutcome` we read back in the failure handler below.
    return await runFetchAuthSession(options, hasLocks, logContext, (outcome) => {
      lockOutcome = outcome;
      phase = "awaiting_amplify";
    });
  } catch (error) {
    const isRefreshTokenReuse = isRefreshTokenReuseError(error);
    const failureMetadata = {
      ...logContext,
      phase,
      lockOutcome,
      isRefreshTokenReuse,
      elapsedMs: Date.now() - startMs,
    };

    if (error instanceof AmplifyAuthSessionTimeoutError) {
      // Self-induced timeout — already emitted as `COGNITO_SESSION_AMPLIFY_TIMEOUT`.
      // Re-reporting it via `logError` only double-counts the same event in
      // Error Tracking (staff-1324: this site and the request interceptor each
      // logged the same timeout). Record an enriched non-error action instead
      // and let the caller decide whether the resulting token failure is
      // error-worthy. Genuine Amplify/Web Locks failures still go to `logError`.
      datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_TIMEOUT, failureMetadata);
    } else if (isRefreshTokenReuse && lockOutcome !== "locked") {
      // Refresh token reuse on a lock-bypassed path is the documented,
      // deliberately-accepted cross-tab race: we skipped the Web Lock
      // (`bypassed_no_locks` / `bypassed_acquire_timeout`) precisely to avoid a
      // frozen hidden tab stalling every request, accepting that a concurrent
      // tab may rotate the refresh token out from under us. Error Tracking
      // groups purely by error fingerprint and ignores metadata, so leaving
      // this on `logError` floods the issue with an outcome we chose to
      // tolerate. Record an enriched non-error action instead. Reuse that
      // happens while we actually HELD the lock falls through to `logError`
      // below — that would mean the lock failed its one job and must stay
      // observable. See staff-1344.
      datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_REFRESH_TOKEN_REUSE, failureMetadata);
    } else {
      logError(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_FAILURE, {
        error,
        metadata: failureMetadata,
      });
    }

    throw error;
  } finally {
    clearTimeout(hangTimer);
  }
}

async function runFetchAuthSession(
  options: FetchAuthSessionOptions | undefined,
  useLock: boolean,
  logContext: Record<string, unknown>,
  onAcquired: (outcome: LockOutcome) => void
): Promise<AuthSessionResult> {
  if (!useLock) {
    // Web Locks API is unavailable (e.g., non-secure context). Run direct;
    // Amplify's intra-tab `deDupeAsyncFunction` still prevents duplicate
    // concurrent refreshes inside this tab.
    datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_BYPASSED, {
      ...logContext,
      reason: "no_locks",
    });
    onAcquired("bypassed_no_locks");
    return amplifyFetchAuthSessionWithTimeout(options);
  }

  const abortController = new AbortController();
  const acquireTimer = setTimeout(() => {
    abortController.abort();
  }, LOCK_ACQUIRE_TIMEOUT_MS);

  try {
    return await navigator.locks.request(
      LOCK_NAME,
      { signal: abortController.signal },
      async () => {
        clearTimeout(acquireTimer);
        onAcquired("locked");
        return amplifyFetchAuthSessionWithTimeout(options);
      }
    );
  } catch (error) {
    if (abortController.signal.aborted) {
      datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_BYPASSED, {
        ...logContext,
        reason: "acquire_timeout",
      });
      onAcquired("bypassed_acquire_timeout");
      return amplifyFetchAuthSessionWithTimeout(options);
    }
    throw error;
  } finally {
    clearTimeout(acquireTimer);
  }
}

async function amplifyFetchAuthSessionWithTimeout(
  options: FetchAuthSessionOptions | undefined
): Promise<AuthSessionResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      amplifyFetchAuthSession(options),
      new Promise<AuthSessionResult>((_, reject) => {
        timer = setTimeout(() => {
          datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_AMPLIFY_TIMEOUT, {
            thresholdMs: AMPLIFY_FETCH_TIMEOUT_MS,
            forceRefresh: options?.forceRefresh ?? false,
          });
          reject(new AmplifyAuthSessionTimeoutError());
        }, AMPLIFY_FETCH_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}
